import type { ContentBannerProps } from '@dashhudson/dashing-ui/content-banner';
import type { FilterSidePanelContent } from '@dashhudson/dashing-ui/filter-side-panel';
import type { IconName } from '@dashhudson/dashing-ui/icon';
import type { MetricTimeFrame, PostType, SortOrderType } from '@dashhudson/dashing-ui/models';
import type { DateRangeModel } from '@dashhudson/dashing-ui/utils/date-ranges';
import type { Component, MaybeRefOrGetter, Ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import type { ReportDataCsvParams } from '@/apis/dashboards';
import type { ContentTagMatchType, MediaFilters, MediaV2 } from '@/apis/queries/media-list.types';
import type { MetricOption } from '@/models/metrics.enum';
import type { PlatformConnection } from '@/models/platform/types';
import type { PermissionObject } from '@/types/auth.types';
import type { BaseContext, Predicate } from '@/utils/predicate-utils';

// dashing-ui does not export QueryParamMappings (lives in its internal utils/query), so
// extract it from the FilterSidePanelContent prop that consumes it. Read off the props
// parameter rather than `InstanceType`: 19.14.x made the component generic over its filter
// shape, and a generic function type has no construct signature.
export type QueryParamMappings = NonNullable<
  Parameters<typeof FilterSidePanelContent>[0]['queryParamMapping']
>;

type FilterOption = {
  label: string;
  value: boolean | string;
};

export type SortGroup = {
  title: string;
  children: MetricOption[];
};

export type SortDetails = {
  options: Array<MetricOption | SortGroup>;
  default: {
    sortOrder: SortOrderType;
    selected: MetricOption;
  };
};

type DownloadConfig = {
  fileName: string;
  /** The export job key: names the store's pending flag and the notification the job completes on. */
  type: string;
  label: string;
  pendingKey: string;
};

type PdfDownloadConfig = DownloadConfig & {
  // Must be a valid key of PdfStoreState (`src/stores/pdf.ts`)
  pendingKey: `${string}ReportPending`;
};

type ShowPinBoardPrivacyFilterContext = {
  platformStore: {
    pinterestAccount?: {
      importProtectedPins?: boolean;
    } | null;
  };
};

type YourPostsFilters = {
  authorship?: string[];
  collaboratorUsernames?: string[];
  captionContain?: string[];
  captionNotContain?: string[];
  containSwipeUpLink?: boolean;
  contentTags: string[];
  contentTagMatchType: ContentTagMatchType;
  distributionType?: string;
  isBoosted?: boolean;
  isCrossposted?: boolean;
  // Not part of any `filters.default`; only present once selected in the filter panel
  mediaType?: string;
  pinBoardPrivacy?: string;
  postType: string[];
  privacy?: string[];
  // Not part of any `filters.default`; only present once selected in the filter panel
  privacyStatus?: string[];
  reportingPeriod: DateRangeModel;
  sort: {
    sortOrder: string;
    selected: MetricOption;
  };
};

export type ScopedYourPostsFilters = Partial<YourPostsFilters>;

export type YourPostsChannelFilters = {
  captionKeywords?: {
    contain?: string[];
    notContain?: string[];
  };
  collaboratorUsernames?: string[];
  contentTags?: string[];
  contentTagMatchType?: ContentTagMatchType;
  distributionType?: string;
  isPrimaryAuthored?: boolean;
  isBoosted?: boolean;
  isCrossposted?: boolean;
  pinBoardPrivacy?: string;
  privacyStatus?: string[];
} & Record<string, unknown>;

export const COLLAB_INVITE_STATUS = {
  ACCEPTED: 'Accepted',
  PENDING: 'Pending',
} as const;

type CollabInviteStatus = (typeof COLLAB_INVITE_STATUS)[keyof typeof COLLAB_INVITE_STATUS];

export type YourPostsFiltersQuery = MediaFilters & {
  brandMediaTypes?: string[];
} & Record<string, unknown>;

export type Collaborator = {
  username: string;
  avatarUrl?: string;
  inviteStatus?: CollabInviteStatus;
};

export type Collaborators = [Collaborator, ...Collaborator[]];

export type YourPostsConfig = {
  title: string;
  channelFilterKey: string;
  adsPlatform?: PlatformConnection;
  includeInstagramUsers?: MaybeRefOrGetter<boolean | undefined>;
  getMediaCardProps?: (data: MediaV2) =>
    | {
        title: string | undefined;
        avatar?: string | { icon: string };
        collaborators?: Collaborators;
      }
    | undefined;
  filters: {
    brandMediaTypes: MaybeRefOrGetter<string[]>;
    default: YourPostsFilters;
    options?: {
      authorship?: FilterOption[];
      distributionType?: FilterOption[];
      isBoosted?: FilterOption[];
      mediaType?: FilterOption[];
      pinBoardPrivacy?: FilterOption[];
      postType?: FilterOption[];
      privacyStatus?: FilterOption[];
    };
    queryParamMapping: QueryParamMappings;
    requestParamsMapping?: Record<string, string>;
    labelOverrides?: Record<string, { singular: string; plural: string }>;
    scope: string;
    sortDetails: SortDetails;
  };
  displayMetrics: MetricOption[];
  metricLabels: MetricOption[];
  download: {
    csv: DownloadConfig;
    pdf: PdfDownloadConfig;
  };
  platformConnection: PlatformConnection;
  captionTitle?: string;
  processFilters?: (
    filters: YourPostsFiltersQuery,
    scopedFilters: ScopedYourPostsFilters | undefined,
  ) => YourPostsFiltersQuery;
  mediaCardDefaults?: {
    metrics: string[];
    labels: Record<string, string>;
  };
  shouldShowDistributionTypeFilter?: boolean;
  shouldShowCrosspostedFilter?: boolean;
  showPinBoardPrivacyFilter?: (context: ShowPinBoardPrivacyFilterContext) => boolean | undefined;
  showPrivacyStatusFilter?: boolean;
};

type OverviewSectionWidth = 'full' | 'half';

type OverviewCsvConfig = {
  label: string;
  filenamePrefix: string;
  layout: 'standard' | 'overview';
  metrics?: MaybeRefOrGetter<readonly string[]>;
  additionalMetrics?: MaybeRefOrGetter<readonly string[]>;
  limit?: number;
};

type OverviewDownloadOption = {
  label: string;
  fileType: 'CSV' | 'PDF';
  fileName: string;
  disabled?: boolean;
};

/**
 * `width` lives on the base rather than on each member because the shell's grid reads it off
 * the un-narrowed `OverviewSection` — a field present on only some members is a TS2339 there.
 * `csv` is shared for the same reason: the shell aggregates every declared export into one
 * `DownloadMenu` without narrowing first.
 */
type OverviewSectionBase = {
  width: OverviewSectionWidth;
  csv?: OverviewCsvConfig | OverviewCsvConfig[];
  /**
   * Drops the whole section when false. Same shape as a metric predicate and filtered through
   * `predicateFilter` with the same context, so a section can follow a flag the way a metric
   * does — or close over a store for a condition the context doesn't carry, like Meta's
   * Promoted Post Insights, which render only while Meta Ads is connected.
   */
  predicate?: Predicate<BaseContext>;
};

type OverviewTrackedSection = {
  /** Mixpanel `section` value, frozen apart from `title` so copy edits and translation never move it. */
  trackingName: string;
};

export type OverviewSeeMoreContext = {
  startDate?: string;
  endDate?: string;
  sort?: MetricOption;
  sortOrder?: SortOrderType;
};

export type OverviewSeeMore = {
  buildRoute: (context: OverviewSeeMoreContext) => RouteLocationRaw;
  hideWhenEmpty?: boolean;
  tracking?: {
    event: string;
    properties: (context: OverviewSeeMoreContext) => Record<string, string | undefined>;
  };
};

/**
 * A sort control owned by one section rather than the page. Instagram renders three feeds with
 * three independent sorts, so the page-level `filters.sortDetails` can't serve them: each section
 * declares its own options under its own filter key, and the shell maps that key to the URL as
 * `<sortKey>` / `<sortKey>Order` (rendered snake_case by the router — `top_video_sort`,
 * `most_engaging_posts_order`). Absent, a section reads the page-level sort under `sort`.
 */
type OverviewSectionSort = {
  /** Scoped-filter key and URL param stem. camelCase; unique per page. */
  sortKey?: string;
  /** Overrides the page-level `filters.sortDetails` for this section alone. */
  sortDetails?: SortDetails;
};

type OverviewTopLineSection = OverviewSectionBase & {
  kind: 'topLine';
  metrics: OverviewTopLineMetric[];
};

export type OverviewTopLineMetric = MetricOption & {
  /** Rows fetched alongside the card metric for its reporting/comparison period hover breakdown. */
  breakdownMetrics?: MetricOption[];
};

export type OverviewChartMetric = MetricOption & {
  /** Plotted when this option is selected. Defaults to the option itself. */
  series?: MetricOption[];
  /** Additional metrics fetched and displayed in the point tooltip without being plotted. */
  tooltipMetrics?: MetricOption[];
};

type OverviewChartCalloutAggregate = 'avg' | 'last' | 'sum';

export type OverviewChartCallout = {
  label: string;
  metric: MetricOption;
  aggregate: OverviewChartCalloutAggregate;
  missingValue?: number;
};

export type OverviewChartCalloutContext = {
  /** Resolved only for charts with a metric selector. */
  selectedMetric?: MetricOption;
  scaleUnit: 'day' | 'month';
};

type OverviewChartCallouts =
  | OverviewChartCallout[]
  | ((context: OverviewChartCalloutContext) => OverviewChartCallout[]);

type OverviewChartSection = OverviewSectionBase &
  OverviewTrackedSection & {
    kind: 'chart';
    title: string;
    titleTooltip?: string;
    metrics: OverviewChartMetric[];
    /** Set renders the selector and plots one option; absent plots every entry in `metrics`. */
    defaultMetric?: MetricOption;
    /** Header stats. Fetched whether or not they are also plotted. */
    callouts?: OverviewChartCallouts;
    postType?: PostType;
    /**
     * URL param for the selected metric, spelled camelCase (`analyticsMetric` renders as
     * `analytics_metric`). Absent keeps the selection out of the URL.
     */
    queryParam?: string;
    tooltipCount?: {
      label: string;
      metric: MetricOption;
    };
  };

export type OverviewAverageQueryContext = {
  channel: string;
  startDate: Readonly<Ref<string | undefined>>;
  endDate: Readonly<Ref<string | undefined>>;
  contextStartDate: Readonly<Ref<string | undefined>>;
  contextEndDate: Readonly<Ref<string | undefined>>;
  /** The option ranking the feed — a query's `appliedSort` where it reports one. */
  sort: Readonly<Ref<MetricOption | undefined>>;
};

type OverviewAverageQueryResult = {
  data: Readonly<Ref<number | null | undefined>>;
};

/**
 * Period average of the ranked metric, drawn as the bar list's marker line. Separate from the
 * posts query because it reads a different report with its own vocabulary, and a failed or absent
 * average only costs the line.
 */
export type OverviewAverageQuery = (
  context: OverviewAverageQueryContext,
) => OverviewAverageQueryResult;

export type OverviewBarListSubMetric = MetricOption & { icon: IconName };

type OverviewBarListSection = OverviewSectionBase &
  OverviewTrackedSection &
  OverviewSectionSort & {
    kind: 'barList';
    seeMore?: OverviewSeeMore;
    // The legacy 65/35 rowspan sidebar isn't reproduced; bar lists always span the grid.
    width: 'full';
    title: string;
    /** Shown in place of `title` while the list is sorted ascending. */
    titleLowest?: string;
    titleTooltip?: string;
    query: OverviewMediaQuery;
    /** Absent draws no average line, matching the legacy sections that had none. */
    average?: OverviewAverageQuery;
    /** Drawn as each row's bar and value; resolved off the media record by `attribute`. */
    barMetric: MetricOption;
    subMetrics?: OverviewBarListSubMetric[];
    /**
     * `false` renders no sort control even when the page declares `sortDetails` — for a feed the
     * backend ranks on its own (YouTube Trending) that shares a page with a grid that does take the
     * sort. The query then receives no sort and the bar follows `barMetric`.
     */
    showSort?: boolean;
    metricTimeFrame?: MetricTimeFrame;
    postType?: PostType;
    /**
     * Shown when the feed resolves empty. For a feed whose backend caps how far back it reads
     * (Top Pins retains ~90 days), the default "No posts found during this date range" misreads
     * as no-content when the range is simply too old — say so here instead.
     */
    noDataMessage?: string;
  };

// `sort` is a library sort field, not a `ContentReportMetricType`: Top Pins and TikTok Trending
// are ranked by their own channel backends.
export type OverviewMediaQueryContext = {
  channel: string;
  startDate: Readonly<Ref<string | undefined>>;
  endDate: Readonly<Ref<string | undefined>>;
  sort: Readonly<Ref<MetricOption | undefined>>;
  sortOrder: Readonly<Ref<SortOrderType | undefined>>;
};

type OverviewMediaQueryResult = {
  csvRequest?: Readonly<Ref<ReportDataCsvParams>>;
  data: Readonly<Ref<MediaV2[] | undefined>>;
  isLoading: Readonly<Ref<boolean>>;
  error: Readonly<Ref<unknown>>;
  /**
   * The option the feed is actually ranked by, where the query can say. A control offering a field
   * the query can't express leaves the feed on its own default, and the section reads this rather
   * than the control so it never calls out a metric the grid isn't sorted by. Absent on a query
   * with no sort control to disagree with.
   */
  appliedSort?: Readonly<Ref<MetricOption | undefined>>;
  skeletonRowCount?: number;
};

export type OverviewMediaQuery = (context: OverviewMediaQueryContext) => OverviewMediaQueryResult;

type OverviewMediaGridSection = OverviewSectionBase &
  OverviewTrackedSection &
  OverviewSectionSort & {
    kind: 'mediaGrid';
    seeMore?: OverviewSeeMore;
    title: string;
    titleTooltip?: string;
    /** Card metrics, read off each post by `attribute`. Not what the grid is sorted by. */
    metrics: MetricOption[];
    query: OverviewMediaQuery;
    printWidth?: number;
    /** Merged over the section's default empty banner; `message` alone is the common override. */
    emptyState?: Partial<ContentBannerProps>;
  };

type OverviewGalleryGridSection = OverviewSectionBase &
  OverviewTrackedSection & {
    kind: 'galleryGrid';
    width: 'full';
    title: string;
    titleTooltip?: string;
    galleryType: string;
    metrics: MetricOption[];
    pageSize: number;
    numMediaType: string;
    sortKey: string;
    sortDetails: SortDetails;
    /** Maps the gallery API's sort keys to dashboard content-report metrics for CSV export. */
    csvSortMetrics?: Record<string, string>;
    emptyState?: Partial<ContentBannerProps>;
  };

export type OverviewDemographicBreakdown =
  | 'AGE_AND_GENDER'
  | 'CITY'
  | 'COUNTRY'
  | 'GENDER'
  | 'LOCATION';

type OverviewDemographicsSection = OverviewSectionBase & {
  kind: 'demographics';
  breakdowns: OverviewDemographicBreakdown[];
};

export type OverviewPromotedPostsMetric = {
  label: string;
  tooltip?: string;
  keys: {
    promoted: string;
    organic?: string;
  };
};

type OverviewPromotedPostsSection = OverviewSectionBase & {
  kind: 'promotedPosts';
  metrics: OverviewPromotedPostsMetric[];
};

/**
 * Closed union — the section dispatcher narrows on `kind`, so adding a member without adding
 * its branch is a type error rather than a silently unrendered section.
 */
export type OverviewSection =
  | OverviewTopLineSection
  | OverviewChartSection
  | OverviewBarListSection
  | OverviewMediaGridSection
  | OverviewGalleryGridSection
  | OverviewDemographicsSection
  | OverviewPromotedPostsSection;

/**
 * The page-level filters, shared by every channel under one scope. Chart metric selections and
 * sorts sit beside them under channel-namespaced keys — see `channelFilterKey`.
 */
export type OverviewFilters = {
  reportingPeriod: DateRangeModel;
  comparisonPeriod?: DateRangeModel;
  viewGraphBy: 'DAILY' | 'MONTHLY';
};

export type OverviewSortState = {
  sortOrder: SortOrderType;
  selected: MetricOption;
};

/**
 * Brand and user guards, each omittable. Never derived from the channel name — Facebook's is
 * `CAN_ACCESS_FB_INSIGHTS`, Instagram's is a bare `CAN_ACCESS_INSTAGRAM` with no `_INSIGHTS`
 * suffix, and Pinterest omits `access` entirely.
 */
type OverviewAccess = {
  brand?: PermissionObject[];
  user?: PermissionObject[];
};

/**
 * Deliberately carries no `channel` field. The channel is the registry key you look the config up
 * by, and the shell already holds it as a prop — restating it here would only add a
 * `constants.ts` -> `types.ts` import cycle for a value every consumer already has.
 */
export type ChannelOverviewConfig = {
  /** Page and report header — `X Overview`, so it can't be spelled from the channel. */
  title: string;
  sections: OverviewSection[];
  filters: {
    default: OverviewFilters;
    options: {
      viewGraphBy?: FilterOption[];
    };
    queryParamMapping: QueryParamMappings;
    requestParamsMapping?: Record<string, string>;
    labelOverrides?: Record<string, { singular: string; plural: string }>;
    // Per-page, rendered by whichever barList / mediaGrid sections the channel declares. Absent on
    // a channel whose feeds are all server-ranked; those sections render with no sort control.
    sortDetails?: SortDetails;
  };
  access?: OverviewAccess;
  platformConnection: PlatformConnection;
  /**
   * Sent with the top-line and chart stats requests. Instagram's legacy page asked for every
   * authorship type so co-authored collab posts count; left unset, the backend intersects what
   * the requested metrics support, and a top line that mixes account metrics (Net New Followers)
   * with post metrics resolves to none, dropping collab posts from every card.
   */
  authorship?: readonly string[];
  /**
   * Banner for a channel whose analytics are turned off. A component, not a name: TikTok's
   * lives in `TikTokAnalyticsDisabledBanner.vue` but registers as
   * `TitTokAnalyticsDisabledBanner` (sic), so nothing here can be spelled from the channel.
   */
  disabledBanner?: Component;
  /**
   * Overrides the shell's default `ReconnectBanner`, which resolves the connection off the
   * registry key — wrong for Facebook, whose analytics connection is `FACEBOOK_ANALYTICS`
   * while the `FACEBOOK` key holds the Instagram connection.
   */
  reconnectBanner?: Component;
  /** Always-rendered informational banners, e.g. Facebook's Meta Ads connect prompt. */
  banners?: Component[];
  download?: {
    options?: OverviewDownloadOption[];
    pdf?: PdfDownloadConfig;
  };
};
